import {
  RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

type Placement =
  | "right"
  | "left"
  | "top"
  | "bottom";

interface UsePortalOptions {
  offset?: number;
  placement?: Placement;
}

interface Position {
  top: number;
  left: number;
}

function calculatePosition(
  triggerRect: DOMRect,
  menuWidth: number,
  menuHeight: number,
  placement: Placement,
  offset: number
): Position {
  switch (placement) {
    case "right":
      return {
        top: triggerRect.top,
        left: triggerRect.right + offset,
      };

    case "left":
      return {
        top: triggerRect.top,
        left: triggerRect.left - menuWidth - offset,
      };

    case "bottom":
      return {
        top: triggerRect.bottom + offset,
        left: triggerRect.left,
      };

    case "top":
      return {
        top: triggerRect.top - menuHeight - offset,
        left: triggerRect.left,
      };
  }
}

export function usePortal(
  triggerRef: RefObject<HTMLElement | null>,
  {
    offset = 15,
    placement = "right",
  }: UsePortalOptions = {}
) {
  const menuRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);

  const [position, setPosition] = useState<Position>({
    top: 0,
    left: 0,
  });

  const updatePosition = () => {
    if (!triggerRef.current) return;

    const triggerRect =
      triggerRef.current.getBoundingClientRect();

    const menuWidth =
      menuRef.current?.offsetWidth ?? 0;

    const menuHeight =
      menuRef.current?.offsetHeight ?? 0;

    setPosition(
      calculatePosition(
        triggerRect,
        menuWidth,
        menuHeight,
        placement,
        offset
      )
    );
  };

  const open = () => {
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
  };

  const toggle = () => {
    setIsOpen((previous) => !previous);
  };


  // Calculate after the portal exists in the DOM
  useLayoutEffect(() => {
    if (!isOpen) return;

    updatePosition();
  }, [
    isOpen,
    placement,
    offset,
  ]);


  // Keep position correct when scrolling/resizing
  useEffect(() => {
    if (!isOpen) return;

    const handleUpdate = () => {
      updatePosition();
    };

    window.addEventListener(
      "resize",
      handleUpdate
    );

    window.addEventListener(
      "scroll",
      handleUpdate,
      true
    );

    return () => {
      window.removeEventListener(
        "resize",
        handleUpdate
      );

      window.removeEventListener(
        "scroll",
        handleUpdate,
        true
      );
    };
  }, [
    isOpen,
    placement,
    offset,
  ]);


  // Close when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (
      event: MouseEvent
    ) => {
      const target = event.target as Node;

      if (
        menuRef.current?.contains(target) ||
        triggerRef.current?.contains(target)
      ) {
        return;
      }

      close();
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, [
    isOpen,
    triggerRef,
  ]);

  return {isOpen, open, close, toggle, menuRef, position};
}